import { inject, Injectable } from "@angular/core";
import { ActivatedRoute, ActivatedRouteSnapshot, Resolve, Router } from "@angular/router";
import { ImportWizardService } from "../../pages/project/map/repository/import-wizard/import-wizard.service";
import { DatasetType, GetImportWizardResponse, JobStatus } from "src/app/dtos/import-wizard/import-wizard.dto";
import { ImportWizardStep, ImportWizardStorage, ImportWizardType } from "../../pages/project/map/repository/import-wizard/import-wizard.storage";
import { firstValueFrom, tap } from "rxjs";
import { ImportWizardTabularService } from "../../pages/project/map/repository/import-wizard/components/tabular/import-wizard-tabular.service";
import { ImportWizardGeoSpatialService } from "../../pages/project/map/repository/import-wizard/components/geospatial/import-wizard-geospatial.service";
import { SharedImportsStorage } from "src/app/pages/shared-imports/shared-imports.storage";
import { ImportWizardGeospatialMinimapService } from "@/app/pages/project/map/repository/import-wizard/components/geospatial/_components/minimap/import-wizard-geospatial-minimap.service";
import { RepositoryService } from "@/app/pages/project/map/repository/repository.service";

@Injectable()
export class ImportWizardResolver implements Resolve<GetImportWizardResponse> {
    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private importWizardService: ImportWizardService,
        private importWizardStorage: ImportWizardStorage,
        private importWizardTabularService: ImportWizardTabularService,
        private importWizardGeoSpatialService: ImportWizardGeoSpatialService,
        private importWizardGeoSpatialMinimap: ImportWizardGeospatialMinimapService,
        private repositoryService: RepositoryService,
        private sharedImportsStorage: SharedImportsStorage
    ) {}

    async resolve(route: ActivatedRouteSnapshot): Promise<GetImportWizardResponse> {
        const id = route.paramMap.get('import_wizard_id')!;
    
        try { 
            const res = await firstValueFrom(
                this.importWizardService.getImportWizard(id)
            );

            this.setupWizard(res);
            await this.getImportDataPreview();
            await this.getHookImportWizardData();
            
            return res;
        } catch (error) {
            this.handleInvalidId();
            return null as any;
        }
    }

    private setupWizard(res: GetImportWizardResponse) {
        this.getType(res.dataset_type as DatasetType);
        this.importWizardStorage.setIsMultiFeatured(res.files?.length > 1);
        this.getStepByStatus(res.status as JobStatus);
        
        this.importWizardStorage.setFilename(res.dataset_name);
        
        this.importWizardStorage.setId(res.id);

        this.importWizardStorage.setInViewStep(
            (this.importWizardStorage.state().MultiFeatured && this.importWizardStorage.step() === ImportWizardStep.PREVIEW) ? 
            ImportWizardStep.FEATURES_LIST : 
            this.importWizardStorage.step()
        );

        this.repositoryService.setSelectedRepository({ repository_id: res.repository_id });
    }

    private async getImportDataPreview() { 
        if(this.importWizardStorage.type() === ImportWizardType.TABULAR) {
            await this.importWizardTabularService.useGetImportDataPreview();
        } else {
            await this.importWizardGeoSpatialService.useGetImportFile();
            await this.importWizardGeoSpatialMinimap.retrieveFileData();
        }
    }

    private async getHookImportWizardData() { 
        if(this.importWizardStorage.type() === ImportWizardType.TABULAR) {
            await this.importWizardTabularService.useGetImportWizard();
        } else {
            await this.importWizardGeoSpatialService.useGetImportWizard();
        }
    }

    

    private getType(dataset_type: DatasetType) {
        const type = dataset_type === DatasetType.TABULAR
        ? ImportWizardType.TABULAR
        : ImportWizardType.GEOSPATIAL;

        this.importWizardStorage.setType(type);

    }

    private getStepByStatus(status: JobStatus) {
        const step = this.importWizardStorage.verifyStepStatusDict(status);
        if(step) this.importWizardStorage.setStep(step);
    }

    private handleInvalidId() { 
        this.router.navigateByUrl(this.sharedImportsStorage.sharedImport()?.import_link ?? '/');
    }
}